import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

// Components
import { Avatar, Button, Col, Text } from 'components';
import { theme } from 'lib';
import Confetti from './Confetti';
import PredictionDial from './PredictionDial';

// The CTA copy is a full sentence, so it has to wrap: Button gives its Text
// wid="100%", which switches on nowrap + ellipsis — whiteSpace undoes that, and
// the auto height lets the pill grow to however many lines it takes.
const WRAPPING_TEXT_STYLE = { whiteSpace: 'normal', lineHt: '20px' };
const PILL_STYLE = { height: 'auto', minHeight: '44px', borderRadius: '12px' };

/*
  Success step of the CTA flow — shown at /shoutout/:username/success after
  the shout-out is posted. Earned points arrive via navigation state from the
  compose step (the /shoutouts/create response), so a direct page load simply
  omits the points line.
*/
const ShoutoutSent = ({ otherUser, onContinueInApp }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { pathname, state } = useLocation();
  const navigate = useNavigate();

  const points = state?.points || 0;

  // The dial hides itself when the team has no upcoming game / the fan already
  // voted; when it's gone the continue button needs a bigger gap below the
  // points text (the dial's own spacing no longer separates them).
  const [dialVisible, setDialVisible] = useState(true);

  // before the half-time vote the button is a plain "Continue in App"; after it
  // the copy sells the full-time prediction, which only lives in the app
  const [hasVotedPrediction, setHasVotedPrediction] = useState(false);

  // celebrate lives in history state, which survives refreshes — capture it
  // once, then scrub it so the confetti only plays on the fresh arrival
  const [celebrate] = useState(() => !!state?.celebrate);

  useEffect(() => {
    // shoutoutUsage only present for non-subscribed users (weekly limit) —
    // same toast the mobile app shows on GotPoint after a shoutout
    if (state?.celebrate && state?.shoutoutUsage) {
      const { used, limit } = state.shoutoutUsage;
      dispatch({
        type: 'HANDLE_RES',
        payload: { res: t('shoutout.shoutoutusage', { used, limit }) }
      });
    }

    if (state?.celebrate) {
      navigate(pathname, { replace: true, state: { ...state, celebrate: false } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Col wid="100%" centerAll noFlex>
      {celebrate && <Confetti />}

      {/* with no dial the screen is sparse — lift the avatar up and size it
          larger so it reads as a hero image */}
      <Col noFlex center centerAll marg={dialVisible ? undefined : '-40px 0 0'}>
        <Avatar user={otherUser} big={dialVisible} large={!dialVisible} showVerified />
      </Col>

      <Text h1 col="#fff" center marg="16px 0 0">
        {t('callToAction.shoutoutSentTitle')}
      </Text>

      {points > 0 && (
        <Text b0 col="#fff" center marg="12px 0 0">
          {/* spanned Text ignores the h3 flag — size matches H3's 24px explicitly */}
          <Trans components={{ highlight: <Text spanned bold size="20px" col="#FFC93C" /> }}>
            {t('callToAction.shoutoutSentPoints', { points })}
          </Trans>
        </Text>
      )}

      {/* Half-Time score prediction for the team's upcoming game (hidden when
          none). The dial handles its own confirm-and-vote flow — no deep link. */}
      <PredictionDial
        teamId={otherUser?._id}
        onVisibilityChange={setDialVisible}
        onVotedChange={setHasVotedPrediction}
      />

      {/* the full-time pitch only makes sense once the half-time vote is in */}
      {hasVotedPrediction && (
        <Button
          big
          wid="90%"
          marg={dialVisible ? '24px 0 0' : '60px 0 0'}
          pad="10px 20px"
          bg={theme.SECONDARY}
          text={t('callToAction.predictFullTimeInApp')}
          textStyle={WRAPPING_TEXT_STYLE}
          style={PILL_STYLE}
          onClick={onContinueInApp}
        />
      )}

      {/* always offered — both buttons deep-link into the app, the copy just
          frames it as skipping the full-time prediction */}
      <Button
        big
        wid="90%"
        marg={hasVotedPrediction ? '12px 0 0' : dialVisible ? '24px 0 0' : '60px 0 0'}
        pad="10px 20px"
        bg={theme.SECONDARY}
        text={t('callToAction.noThanksContinueToChannel', { name: otherUser?.name })}
        textStyle={WRAPPING_TEXT_STYLE}
        style={PILL_STYLE}
        onClick={onContinueInApp}
      />
    </Col>
  );
};

export default ShoutoutSent;
